import React, { useContext } from 'react';
import HomePage from './components/pages/HomePage';
import LeagueTablePage from './components/pages/LeagueTablePage';
import FixturesPage from './components/pages/FixturesPage';
import Layout from './components/Layout';
import FormTablePage from './components/pages/FormTablePage';
import ResultsPage from './components/pages/ResultsPage';
import HistoryPage from './components/pages/HistoryPage';
import StatsPage from './components/pages/StatsPage';
import AdminLoginPage from './components/pages/AdminLoginPage';
import EnterResultsPage from './components/pages/EnterResultsPage';
import EditFixturesPage from './components/pages/EditFixturesPage';
import SeasonsPage from './components/pages/SeasonsPage';
import PlayersPage from './components/pages/PlayersPage';
import { Routes, Route, Navigate } from 'react-router-dom';
import { green, grey } from '@mui/material/colors';
import { Container, ThemeProvider, createTheme, responsiveFontSizes, CssBaseline } from '@mui/material';
import useStickyState from './utils/useStickyState';
import { tokenContext } from './components/TokenProvider';
import { backendCheckToken } from './backend/backend';

const getThemePalette = (dark) => (
  createTheme({
    palette: {
      ...(dark===false
        ? {
            primary: {
              main: green[700],
              lines: '#cccccc'
            },
            secondary: {
              main: green[100]
            },
            background: {
              highlight: '#eeeeee',
              paper: '#eeeeee'
            }
          }
        : {
            primary: {
              main: green[700],
              lines: '#555555'
            },
            secondary: {
              main: green[900]
            },
            background: {
              default: '#181818',
              highlight: '#333333',
              paper: '#333333'
            },
            text: {
              primary: grey[50],
              secondary: grey[600],
              disabled: grey[800]
            },
            divider: '#303030'
          }),
    }
  })
);

const assembleTheme = (baseTheme) => {
  var theme = createTheme(baseTheme,{
    typography: {
      fontFamily: 'Roboto',
      h1: {
        fontWeight: 'normal',
        fontSize: '4em'
      },
      h2: {
        fontWeight: 'normal',
        fontSize: '2.5em'
      },
      h3: {
        fontWeight: 'normal',
        fontSize: '2.2em'
      },
      h4: {
        fontWeight: 'normal',
        fontSize: '1.8em'
      },
      h5: {
        fontWeight: 'bold',
        fontSize: '1.2em'
      },
      body1: {
        fontFamily: 'Lato'
      },
      body2: {
        fontFamily: 'Lato',
        fontSize: '1em',
        fontWeight: 'bold'
      },
      button: {
        fontFamily: 'Roboto'
      },
      spacing: 120
    }
  });

  theme=createTheme(theme, {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '4px',
            borderColor: theme.palette.primary.lines
          }
        }
      },
      MuiPaper: {
        defaultProps: {
          variant: 'outlined',
        }
      },
      MuiStack: {
        defaultProps: {
          spacing: 4
        }
      },
      MuiTableHead: {
        defaultProps: {
          bgcolor: theme.palette.secondary.main
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorPrimary: {
            fill: theme.palette.primary.main
          }
        }
      },
      MuiTabs: {
        defaultProps: {
          textColor: 'inherit'
        }
      },
      MuiTextField: {
        defaultProps: {
          size: 'small'
        }
      }
    }
  });

  theme=responsiveFontSizes(theme,{factor:5});
  return theme;
}

function App() {
  const [dark,setDark]=useStickyState(false,"dark");
  const {token,lastAdmin,dropAdmin}=useContext(tokenContext);

  const theme=React.useMemo( () => assembleTheme(getThemePalette(dark)),[dark]);

  const setDarkMode= (event) => {
    setDark(event.target.checked);
  }

  const checkAdmin = (adminElement) => {
    if (token===null) {
      return <Navigate to="/admin"/>
    } else {
      return adminElement;
    }
  }

  if (token) {
    backendCheckToken(token).then((valid)=>{
      if (!valid)
        dropAdmin();
    });
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container sx={{bgcolor:'background.default', color:'text.primary', paddingX:{xs:1,sm:2}}} disableGutters>
        <Routes>
          <Route path="/" element={<Layout darkMode={dark} setDarkMode={setDarkMode} />}>
            <Route path="" element={<Navigate to="/home" />} />
            <Route path="home" element={<HomePage/>} />
            <Route path="fixtures" element={<FixturesPage/>} />
            <Route path="leaguetable" element={<LeagueTablePage/>} />
            <Route path="form" element={<FormTablePage/>} />
            <Route path="results" element={<ResultsPage/>} />
            <Route path="history" element={<HistoryPage/>} />
            <Route path="stats" element={<StatsPage/>} />
            <Route path="admin" element={token!=null ? <Navigate to={lastAdmin}/> : <AdminLoginPage />}/>
            <Route path="adminresults" element={checkAdmin(<EnterResultsPage />)} />
            <Route path="adminfixtures" element={checkAdmin(<EditFixturesPage />)} />
            <Route path="adminseasons" element={checkAdmin(<SeasonsPage />)} />
            <Route path="adminplayers" element={checkAdmin(<PlayersPage />)} />
            <Route path="tokenresults/:token" element={<EnterResultsPage token/>} />
            <Route path="*" element={<Navigate to="/home" />} />
          </Route>
        </Routes>
      </Container>
    </ThemeProvider>
  );
}

export default App;
